import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, Info, Users, Building, CheckCircle, XCircle } from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import { DataTable2 } from '../components/ui/DataTable2';
import { DataTable } from '../components/ui/DataTable'; // ← NEW IMPORT
import FastAPIClient from '../services/apiClient';
import { useToast } from '../hooks/useToast';
import { fetchStores, fetchUtilStaff, fetchStoreWithStaff } from '../apiHelper'; // ← ADDED fetchStoreWithStaff
const client = new FastAPIClient();

const StaffToStore = () => {
    const navigate = useNavigate();
    const location = useLocation(); // ← NEW: to read optional state
    const { showToast } = useToast();
    const [user, setUser] = useState(null);

    // State for stores
    const [stores, setStores] = useState([]);
    const [loadingStores, setLoadingStores] = useState(true);
    const [errorStores, setErrorStores] = useState(null);
    const [selectedStore, setSelectedStore] = useState(null); // Holds the selected store object

    // State for staff members (available to assign)
    const [staffMembers, setStaffMembers] = useState([]);
    const [loadingStaff, setLoadingStaff] = useState(true);
    const [errorStaff, setErrorStaff] = useState(null);
    const [selectedStaffIds, setSelectedStaffIds] = useState(new Set());

    // NEW: State for staff already assigned to the selected store
    const [assignedStaff, setAssignedStaff] = useState([]);
    const [loadingAssigned, setLoadingAssigned] = useState(false);
    const [errorAssigned, setErrorAssigned] = useState(null);

    // State for assignment loading
    const [isAssigning, setIsAssigning] = useState(false);

    const [removingId, setRemovingId] = useState(null);

    // Effect to load user from local storage
    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast("Please log in to manage staff assignments.", "info");
            navigate('/login');
        } else {
            setUser(JSON.parse(lcUser));
        }
    }, [showToast, navigate]);

    // Fetch stores
    useEffect(() => {
        const loadStores = async () => {
            if (!user) return;
            setLoadingStores(true);
            setErrorStores(null);
            try {
                const fetchedStores = await fetchStores(user);
                setStores(fetchedStores.map(store => ({
                    label: store.identifier,
                    value: store.store_id,
                    storeData: store
                })));
            } catch (err) {
                const errorMessage = err.response?.data?.detail || `Failed to load stores: ${err.message || err}`;
                setErrorStores(errorMessage);
                setTimeout(() => showToast(errorMessage, 'error'), 0);
            } finally {
                setLoadingStores(false);
            }
        };
        loadStores();
    }, [user, showToast]);

    // Fetch staff members (all util staff)
    useEffect(() => {
        const loadStaff = async () => {
            if (!user) return;
            setLoadingStaff(true);
            setErrorStaff(null);
            try {
                const fetchedStaff = await fetchUtilStaff(user);
                setStaffMembers(fetchedStaff.map(staff => ({
                    id: staff.member_id,
                    name: `${staff.user.first_name} ${staff.user.last_name}`,
                    email: staff.user.credential.logon_id,
                    staffData: staff
                })));
            } catch (err) {
                const errorMessage = err.response?.data?.detail || `Failed to load staff members: ${err.message || err}`;
                setErrorStaff(errorMessage);
                setTimeout(() => showToast(errorMessage, 'error'), 0);
            } finally {
                setLoadingStaff(false);
            }
        };
        loadStaff();
    }, [user, showToast]);

    // NEW: Load assigned staff when a store is selected
    const loadAssignedStaff = useCallback(async (storeId) => {
        if (!storeId) {
            setAssignedStaff([]);
            return;
        }
        setLoadingAssigned(true);
        setErrorAssigned(null);
        try {
            const result = await fetchStoreWithStaff(storeId);
            setAssignedStaff(result.staff.map(s => ({
                id: s.member_id,
                name: `${s.user.first_name} ${s.user.last_name}`,
                email: s.user.credential.logon_id,
                staffData: s
            })));
        } catch (err) {
            const errorMessage = err.response?.data?.detail || `Failed to load assigned staff: ${err.message || err}`;
            setErrorAssigned(errorMessage);
            setTimeout(() => showToast(errorMessage, 'error'), 0);
        } finally {
            setLoadingAssigned(false);
        }
    }, [showToast]);

    // Handle store selection change (from dropdown)
    const handleStoreSelect = useCallback((e) => {
        const selectedOption = stores.find(opt => opt.value === parseInt(e.target.value));
        setSelectedStore(selectedOption ? selectedOption.storeData : null);
        setSelectedStaffIds(new Set());
        if (selectedOption) {
            loadAssignedStaff(selectedOption.value);
        } else {
            setAssignedStaff([]);
        }
    }, [stores, loadAssignedStaff]);

    // NEW: On mount, check for optional navigation state
    useEffect(() => {
        if (location.state?.branch_id && stores.length > 0) {
            const preselected = stores.find(s => s.value === location.state.branch_id);
            if (preselected) {
                setSelectedStore(preselected.storeData);
                loadAssignedStaff(preselected.value);
            }
        }
    }, [location.state, stores, loadAssignedStaff]);


    const handleRemoveStaff = useCallback(async (row) => {
        if (!selectedStore) {
            showToast("No store selected.", "error");
            return;
        }

        setRemovingId(row.id);

        if (!window.confirm(`Remove ${row.name} from ${selectedStore.identifier}?`)) {
            return;
        }

        try {
            setAssignedStaff(prev => prev.filter(s => s.id != row.id));
            showToast("Removing staff...", "info");

            const payload = {
                store_id: selectedStore.store_id,
                staff_id: row.staffData.member_id,
            }
            const response = await client.postData('/users/remove-staff-from-store', payload)
            showToast(response?.msg || "Staff removed successfully.", "success");
            
            await loadAssignedStaff(selectedStore.store_id);

        } catch (err) {
            console.error("Failed to remove staff:", err)
            await loadAssignedStaff(selectedStore.store_id);
            const errorMessage = err.response?.data?.detail ||
                err.response?.data?.msg ||
                err.message ||
                "Failed to remove staff. Please try again.";
            showToast(errorMessage, "error");
        } finally {
            setRemovingId(null);
        }

    }, [selectedStore, loadAssignedStaff, showToast])

    // Handle staff member selection in DataTable
    const handleStaffSelect = useCallback((staffId) => {
        setSelectedStaffIds(prevSelected => {
            const newSelected = new Set(prevSelected);
            if (newSelected.has(staffId)) {
                newSelected.delete(staffId);
            } else {
                newSelected.add(staffId);
            }
            return newSelected;
        });
    }, []);

    // DataTable columns for staff members (available)
    const staffColumns = useMemo(() => [
        {
            key: 'select',
            label: 'Select',
            render: (value, row) => (
                <input
                    type="checkbox"
                    className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out rounded focus:ring-indigo-500"
                    checked={selectedStaffIds.has(row.id)}
                    onChange={() => handleStaffSelect(row.id)}
                />
            )
        },
        {
            key: 'name',
            label: 'Staff Name',
            render: (name) => (
                <div className="flex items-center gap-2">
                    <Users size={16} className="text-blue-500" />
                    <span>{name}</span>
                </div>
            )
        },
        { key: 'email', label: 'Email' },
    ], [selectedStaffIds, handleStaffSelect]);

    // NEW: Columns for assigned staff table (read-only)
    const assignedColumns = useMemo(() => [
        {
            key: 'name',
            label: 'Staff Name',
            render: (name) => (
                <div className="flex items-center gap-2">
                    <Users size={16} className="text-green-600" />
                    <span>{name}</span>
                </div>
            )
        },
        { key: 'email', label: 'Email' },
        {
            key: 'actions',
            label: 'Actions',
            render: (_, row) => (
                <div className="flex justify-end">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveStaff(row)
                        }}
                        disabled={removingId === row.id}
                        className="p-2 rounded-full hover:bg-red-50 text-red-600 transition-colors disabled:opacity-50"
                        title="Remove from this branch">
                            {removingId === row.id ? (
                                <Loader2 size={20} className="animate-spin" />
                            ) : (
                                <XCircle size={20} />
                            )}
                    </button>
                </div>
            )
        }
    ], [handleRemoveStaff]);

    // Handle assignment submission
    const handleAssignStaff = useCallback(async () => {
        if (!selectedStore) {
            setTimeout(() => showToast("Please select a store first.", 'error'), 0);
            return;
        }
        if (selectedStaffIds.size === 0) {
            setTimeout(() => showToast("Please select at least one staff member to assign.", 'error'), 0);
            return;
        }
        setIsAssigning(true);
        try {
            const payload = Array.from(selectedStaffIds).map(staffId => ({
                store_id: selectedStore.store_id,
                staff_id: staffId
            }));
            const response = await client.postData('/store/assign-staff-to-store', payload);
            setTimeout(() => showToast(response.msg || "Staff assigned successfully!", 'success'), 0);
            setSelectedStaffIds(new Set());
            // Refresh assigned staff list
            loadAssignedStaff(selectedStore.store_id);
        } catch (err) {
            console.error("Error assigning staff:", err);
            const errorMessage = err.response?.data?.detail || `Failed to assign staff: ${err.message || err}`;
            setTimeout(() => showToast(errorMessage, 'error'), 0);
        } finally {
            setIsAssigning(false);
        }
    }, [selectedStore, selectedStaffIds, showToast, loadAssignedStaff]);

    if (!user) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-orange-600'>
                <Info size={32} className="mb-2" />
                <p className="text-lg mb-4">Please log in to manage staff assignments.</p>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
            <Card className="mb-6 p-4 sm:p-6 shadow-lg rounded-lg">
                <h2 className="text-2xl font-bold text-text-primary mb-4 flex items-center gap-2">
                    <Building size={24} className="text-indigo-600" /> Assign Staff to Branch
                </h2>
                <p className="text-text-secondary mb-6">
                    Select a branch and then choose the staff members you wish to assign to it.
                </p>
                {/* Store Selection */}
                {loadingStores ? (
                    <div className="flex items-center justify-center py-4">
                        <Loader2 className="animate-spin h-5 w-5 mr-2 text-blue-500" />
                        <p className="text-gray-600">Loading stores...</p>
                    </div>
                ) : errorStores ? (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-4" role="alert">
                        <p>{errorStores}</p>
                    </div>
                ) : (
                    <div className="mb-6">
                        <SearchableSelectBox
                            label="Select Branch"
                            id="store-select"
                            name="store-select"
                            value={selectedStore?.store_id || ''}
                            onChange={handleStoreSelect}
                            options={stores}
                            placeholder="Choose a branch"
                            onSearchChange={() => { }}
                            loading={loadingStores}
                            error={errorStores}
                            icon={<Building size={18} />}
                        />
                        {selectedStore && (
                            <p className="text-sm text-gray-600 mt-2">
                                Selected Branch: <span className="font-semibold">{selectedStore.identifier}</span>
                            </p>
                        )}
                    </div>
                )}
            </Card>

            {/* NEW: Assigned Staff Table */}
            {/* NEW: Responsive Two-Column Layout for Tables */}
            {selectedStore ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    {/* Assigned Staff Table */}
                    <Card className="p-4 sm:p-6 shadow-lg rounded-lg h-fit">
                        <h2 className="text-xl sm:text-2xl font-bold text-text-primary mb-4 flex items-center gap-2">
                            <Users size={24} className="text-green-600" /> Currently Assigned
                        </h2>
                        {loadingAssigned ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="animate-spin h-5 w-5 mr-2 text-blue-500" />
                                <p className="text-gray-600">Loading assigned staff...</p>
                            </div>
                        ) : errorAssigned ? (
                            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md" role="alert">
                                <p>{errorAssigned}</p>
                            </div>
                        ) : (
                            <DataTable
                                columns={assignedColumns}
                                data={assignedStaff}
                                title={`${assignedStaff.length} staff assigned`}
                                classNames="mt-2"
                            />
                        )}
                    </Card>

                    {/* Select Staff Table + Assign Button */}
                    <Card className="p-4 sm:p-6 shadow-lg rounded-lg h-fit">
                        <h2 className="text-xl sm:text-2xl font-bold text-text-primary mb-4 flex items-center gap-2">
                            <Users size={24} className="text-green-600" /> Add Staff Members
                        </h2>
                        <p className="text-text-secondary text-sm mb-4">
                            Check staff below to assign to <span className="font-semibold">{selectedStore.identifier}</span>
                        </p>
                        {loadingStaff ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="animate-spin h-5 w-5 mr-2 text-blue-500" />
                                <p className="text-gray-600">Loading staff members...</p>
                            </div>
                        ) : errorStaff ? (
                            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-4" role="alert">
                                <p>{errorStaff}</p>
                            </div>
                        ) : (
                            <>
                                <DataTable2
                                    columns={staffColumns}
                                    data={staffMembers}
                                    loading={loadingStaff}
                                    error={errorStaff}
                                    emptyMessage="No staff members found."
                                    enableSearch={true}
                                    enablePagination={true}
                                    rowsPerPage={10}
                                />
                                <div className="mt-6 flex justify-end">
                                    <Button
                                        onClick={handleAssignStaff}
                                        variant="primary"
                                        size="lg"
                                        disabled={!selectedStore || selectedStaffIds.size === 0 || isAssigning}
                                        className="flex items-center justify-center gap-2"
                                    >
                                        {isAssigning ? (
                                            <>
                                                <Loader2 className="animate-spin h-5 w-5" /> Assigning...
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle size={20} /> Assign ({selectedStaffIds.size})
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </>
                        )}
                    </Card>
                </div>
            ) : (
                /* Original single card when no store selected */
                <Card className="p-4 sm:p-6 shadow-lg rounded-lg">
                    <h2 className="text-2xl font-bold text-text-primary mb-4 flex items-center gap-2">
                        <Users size={24} className="text-green-600" /> Select Staff Members
                    </h2>
                    <p className="text-text-secondary mb-6">
                        Please select a branch above to assign staff.
                    </p>
                    {/* ... existing DataTable2 and button ... */}
                </Card>
            )}

        </div>
    );
};

export default StaffToStore;